import { NgClass, NgOptimizedImage, NgStyle } from '@angular/common'
import { Component, OnInit, inject, signal, WritableSignal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'

import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from 'primeng/accordion'
import { InputTextModule } from 'primeng/inputtext'
import { PaginatorModule } from 'primeng/paginator'
import { Select } from 'primeng/select'
import { SkeletonModule } from 'primeng/skeleton'
import { finalize } from 'rxjs/operators'

import { CategoryFilterComponent } from 'app/_shared/components/filter-tree/category-filter.component'
import { MobileFiltersComponent } from 'app/_shared/components/mobile-filters/mobile-filters.component'
import { ProductItemComponent } from 'app/_shared/components/product-item/product-item.component'
import { Category, SortOption, Filters, SortDescriptor } from 'app/_shared/interfaces/Category'
import { Product } from 'app/_shared/interfaces/Product'
import { LayoutService } from 'app/_shared/services/layout.service'
import { ProductService } from 'app/_shared/services/product.service'
import { AppConfigService } from 'app/app-config.service'
import { ConfigStore } from 'app/store/config.store'

@Component({
  selector: 'app-categories',
  templateUrl: './categories.component.html',
  imports: [
    PaginatorModule,
    SkeletonModule,
    InputTextModule,
    Select,
    ProductItemComponent,
    CategoryFilterComponent,
    NgStyle,
    NgClass,
    NgOptimizedImage,
    MobileFiltersComponent,
    FormsModule,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent
  ],
  styleUrl: './categories.component.scss'
})
export class CategoriesComponent implements OnInit {
  private productService = inject(ProductService)
  private appConfigService: AppConfigService = inject(AppConfigService)
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute)
  private configStore = inject(ConfigStore)
  private layout = inject(LayoutService)

  selectedCategoryIds: WritableSignal<number[]> = signal<number[]>([])
  hasCategories = true
  category: Category
  categoryLoading: boolean = false

  products: Product[] = []
  productsPerRow = 4
  isLoading: boolean = false
  serverErrors: { [key: string]: any } = {}

  search: string = ''
  sortingOptions: SortOption[] = [
    { label: 'Newest', value: { id: 0, name: 'created_at', code: 'desc' } },
    { label: 'Price Low to High', value: { id: 1, name: 'final_price', code: 'asc' } },
    { label: 'Price High to Low', value: { id: 2, name: 'final_price', code: 'desc' } },
    { label: 'Name Asc', value: { id: 3, name: 'name', code: 'asc' } },
    { label: 'Name Desc', value: { id: 4, name: 'name', code: 'desc' } }
  ]
  selectedSort?: SortDescriptor
  mobileFiltersVisible = false
  isMobile = this.layout.isMobile

  paginatorOptions = {
    total: 0,
    rows: 9,
    first: 0,
    last_page: 0
  }
  skeletons: number[] = Array(6)
    .fill(0)
    .map((_, index) => index + 1)

  storefrontId: number

  showFilters = false

  ngOnInit(): void {
    this.storefrontId = this.appConfigService.getAppConfig?.id
    this.productsPerRow = this.configStore.config()?.settings?.productsPerRow ?? 4

    this.paginatorOptions = {
      ...this.paginatorOptions,
      rows: this.configStore.config()?.settings?.productsPerPage ?? 9
    }
    // Get slug from the route
    this.activatedRoute.paramMap.subscribe(params => {
      const slug = params.get('slug')
      if (slug) {
        this.fetchCategoryBySlug(slug)
      }
    })

    const settingsSortRaw = this.configStore.config()?.settings?.sortBy as
      SortDescriptor | string | undefined

    const settingsSort: SortDescriptor | undefined =
      typeof settingsSortRaw === 'string'
        ? (JSON.parse(settingsSortRaw) as SortDescriptor)
        : settingsSortRaw

    if (settingsSort && settingsSort.name !== 'dont_sort') {
      this.selectedSort = this.sortingOptions.find(o => o.value.id === settingsSort.id)?.value
    } else {
      this.selectedSort = undefined
    }
  }

  fetchCategoryBySlug(slug: string) {
    this.categoryLoading = true
    this.productService
      .getProductCategoryBySlug(this.storefrontId, slug)
      .pipe(finalize(() => (this.categoryLoading = false)))
      .subscribe({
        next: res => {
          this.category = res.data
          this.selectedCategoryIds.set([res.data.id])
          // After fetching the category, fetch products for that category
          this.applyFilters()
        },
        error: err => {
          console.error('Error fetching category by slug:', err)
        }
      })
  }

  applyFilters() {
    // Reset to the first page whenever filters change
    this.paginatorOptions.first = 0
    this.fetchProducts()
  }

  fetchProducts() {
    if (!this.category.id) {
      return
    }
    this.isLoading = true
    const pageNumber = Math.floor(this.paginatorOptions.first / this.paginatorOptions.rows) + 1

    const filters: Filters = {
      global_filter: this.search,
      sort_field: this.selectedSort?.name,
      sort_order: this.selectedSort?.code,
      'product_categories[]': this.selectedCategoryIds(),
      ...(this.category.is_company ? { product_category_id: this.category.id } : {})
    }

    const query = {
      ...filters,
      rows: this.paginatorOptions.rows,
      page: pageNumber
    }

    this.productService.getProducts(this.storefrontId, query).subscribe({
      next: response => {
        this.products = response.data.data
        this.paginatorOptions.total = response.data.total
        this.paginatorOptions.last_page = response.data.last_page
        this.showFilters = !!this.products.length
        this.isLoading = false
      },
      error: err => {
        this.serverErrors = err.error.message
        this.isLoading = false
      }
    })
  }

  onPageChange(event: any) {
    this.paginatorOptions.rows = event.rows ?? 9
    this.paginatorOptions.first = event.first ?? 0
    this.fetchProducts()
  }
}
